import { Box, BoxProps } from "@chakra-ui/react";
import { PropsWithChildren, useRef, useState } from "react";

import { useAutoScrollToBottom, useOnlyOnce, useRect } from "@nens/react/hooks";

interface AnimateSizeProps extends PropsWithChildren<BoxProps> {
  defaultToBottom?: boolean;
  dontAnimate?: boolean;
  animateWidth?: boolean; // Only use width animation if the width should be based on the content and not the container, this will ignore maxW and w of the container
}

const TRANSITION_TIME = 300;

export function AnimateSize({
  children,
  defaultToBottom,
  dontAnimate,
  animateWidth = false,
  ...props
}: AnimateSizeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const { height: containerHeight } = useRect(containerRef);
  const { height, width } = useRect(innerRef);

  const container = containerRef.current;
  const containerIsOverflowing = container ? container.scrollHeight > containerHeight : false;
  // Stop the height animation if we are defaulting to bottom and the container is overflowing
  // Because then the animation is the fact that we scroll to bottom
  // Or if we don't want to animate at all
  const stopHeightAnimation = (defaultToBottom && containerIsOverflowing) || dontAnimate;
  const stopWidthAnimation = !animateWidth || dontAnimate;

  // If we want to we scroll the element to bottom on overflow
  useAutoScrollToBottom(defaultToBottom ? containerRef.current : null);

  // Have a small delay on starting to animate so on the first render it doesn't animate
  const [isInitialRender, setIsInitialRender] = useState(true);
  useOnlyOnce(() => {
    setTimeout(() => {
      setIsInitialRender(false);
    });
  });

  if (stopHeightAnimation && stopWidthAnimation) {
    return (
      <Box {...props} ref={containerRef}>
        <Box position="relative" ref={innerRef}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box {...props} ref={containerRef}>
      <Box
        overflow="hidden"
        transition={
          [
            !stopHeightAnimation ? `height ${TRANSITION_TIME}ms` : undefined,
            !stopWidthAnimation ? `width ${TRANSITION_TIME}ms` : undefined,
          ]
            .filter(Boolean)
            .join(", ") || undefined
        }
        position="relative"
        // This +1 is to prevent the height from getting a mysterious min-height that isn't defined anywhere
        h={isInitialRender ? undefined : height + 1}
        w={isInitialRender || !animateWidth ? undefined : width}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={animateWidth ? undefined : 0}
          ref={innerRef}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
